import type { VenueReservation } from "@/graphql/operations";
import { parse } from "date-fns";

/**
 * Returns a sortable timestamp for a reservation's start time.
 */
export const getReservationTimestamp = (reservation: VenueReservation) => {
  const date = reservation.group?.date;
  const time = reservation.group?.time ?? reservation.reservationTime;
  if (date && time) {
    const normalizedTime = time.length === 5 ? `${time}:00` : time;
    const parsed = parse(
      `${date}-${normalizedTime}`,
      "yyyy-MM-dd-HH:mm:ss",
      new Date(),
    );
    const parsedTime = parsed.getTime();
    if (!Number.isNaN(parsedTime)) return parsedTime;
  }
  const createdTime = new Date(reservation.created).getTime();
  return Number.isNaN(createdTime) ? 0 : createdTime;
};

/**
 * Returns the earliest or latest reservation date in a list.
 */
export const getEdgeReservationDate = (
  reservations: VenueReservation[],
  edge: "start" | "end",
) => {
  if (reservations.length === 0) return null;

  const reservationsWithGroupDate = reservations.filter(
    (reservation) => Boolean(reservation.group?.date),
  );
  const candidates =
    reservationsWithGroupDate.length > 0 ? reservationsWithGroupDate : reservations;

  const getSortableDateTimestamp = (reservation: VenueReservation) => {
    const dateString = reservation.group?.date;
    if (dateString) {
      const dateTime = new Date(`${dateString}T00:00:00`);
      const parsedTime = dateTime.getTime();
      if (!Number.isNaN(parsedTime)) return parsedTime;
    }
    return getReservationTimestamp(reservation);
  };

  const sorted = [...candidates].sort(
    (left, right) => getSortableDateTimestamp(left) - getSortableDateTimestamp(right),
  );
  const reservation =
    edge === "start" ? sorted[0]! : sorted[sorted.length - 1]!;
  const dateString = reservation.group?.date;
  if (dateString) {
    return new Date(`${dateString}T00:00:00`);
  }
  const created = new Date(reservation.created);
  return Number.isNaN(created.getTime()) ? null : created;
};
